import { useState } from "react";
import { Star, Upload, Camera, CheckCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PostCallRatingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  partnerId: string;
  partnerName: string;
  skillId?: string;
  isUser1: boolean;
  onComplete: () => void;
}

const PostCallRatingDialog = ({
  open,
  onOpenChange,
  sessionId,
  partnerId,
  partnerName,
  skillId,
  isUser1,
  onComplete,
}: PostCallRatingDialogProps) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"rating" | "verification" | "complete">("rating");
  const { toast } = useToast();

  const handleScreenshotUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast({
        title: "Please rate your session",
        description: "Select 1-5 stars to rate your learning experience",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Update session with rating
      const ratingField = isUser1 ? "rating_from_user1" : "rating_from_user2";
      const { error: sessionError } = await supabase
        .from("sessions")
        .update({ [ratingField]: rating })
        .eq("id", sessionId);

      if (sessionError) throw sessionError;

      // Also update ratings table for the partner
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Check if rating exists
        const { data: existingRating } = await supabase
          .from("ratings")
          .select("id")
          .eq("rater_id", user.id)
          .eq("rated_user_id", partnerId)
          .single();

        if (existingRating) {
          await supabase
            .from("ratings")
            .update({ rating, updated_at: new Date().toISOString() })
            .eq("id", existingRating.id);
        } else {
          await supabase.from("ratings").insert({
            rater_id: user.id,
            rated_user_id: partnerId,
            rating,
          });
        }
      }

      setStep("verification");
    } catch (error: any) {
      console.error("Error submitting rating:", error);
      toast({
        title: "Error",
        description: "Failed to submit rating. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVerification = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let screenshotUrl = null;

      // Upload screenshot if provided
      if (screenshot) {
        const fileExt = screenshot.name.split(".").pop();
        const fileName = `${user.id}/${sessionId}-${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("challenge-submissions")
          .upload(fileName, screenshot);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("challenge-submissions")
          .getPublicUrl(fileName);

        screenshotUrl = urlData.publicUrl;

        // Save verification record
        await supabase.from("skill_verifications").insert({
          session_id: sessionId,
          user_id: user.id,
          skill_id: skillId || null,
          screenshot_url: screenshotUrl,
          notes,
        });
      }

      setStep("complete");
      toast({
        title: "Session Complete!",
        description: "Your rating and verification have been submitted.",
      });

      setTimeout(() => {
        onComplete();
        onOpenChange(false);
      }, 2000);
    } catch (error: any) {
      console.error("Error submitting verification:", error);
      toast({
        title: "Error",
        description: "Failed to submit verification. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const skipVerification = () => {
    setStep("complete");
    toast({
      title: "Rating Submitted!",
      description: "Session will be marked complete once your partner also rates.",
    });
    setTimeout(() => {
      onComplete();
      onOpenChange(false);
    }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === "rating" && "Rate Your Session"}
            {step === "verification" && "Verify Skill Exchange"}
            {step === "complete" && "Session Complete!"}
          </DialogTitle>
          <DialogDescription>
            {step === "rating" && `How was your session with ${partnerName}?`}
            {step === "verification" && "Upload a screenshot to verify the skill exchange (optional)"}
            {step === "complete" && "Thank you for completing your session!"}
          </DialogDescription>
        </DialogHeader>

        {step === "rating" && (
          <div className="space-y-6">
            {/* Star Rating */}
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-10 w-10 transition-colors ${
                      star <= (hoveredRating || rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {rating === 0 && "Tap to rate"}
              {rating === 1 && "Poor"}
              {rating === 2 && "Fair"}
              {rating === 3 && "Good"}
              {rating === 4 && "Very Good"}
              {rating === 5 && "Excellent!"}
            </p>

            <Button
              onClick={handleSubmitRating}
              disabled={loading || rating === 0}
              className="w-full gradient-primary text-white"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Continue
            </Button>
          </div>
        )}

        {step === "verification" && (
          <div className="space-y-4">
            {/* Screenshot Upload */}
            <div>
              <Label>Screenshot Verification</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Upload a screenshot of your session to verify the skill exchange
              </p>

              {screenshotPreview ? (
                <div className="relative">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setScreenshot(null);
                      setScreenshotPreview(null);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                  <Camera className="h-8 w-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">
                    Click to upload screenshot
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="What did you learn? Any feedback?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={skipVerification} className="flex-1">
                Skip
              </Button>
              <Button
                onClick={handleSubmitVerification}
                disabled={loading}
                className="flex-1 gradient-primary text-white"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Submit
              </Button>
            </div>
          </div>
        )}

        {step === "complete" && (
          <div className="flex flex-col items-center py-6">
            <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
            <p className="text-center text-muted-foreground">
              When both you and your partner complete the rating, your session count will increase!
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PostCallRatingDialog;